import { Program } from '../Models/program.model';

/**
 * Pure helpers for translating between the legacy single-category program
 * fields (CategoryId / SubcategoryId / SubcategoryIds) and the multi-category
 * shape (CategoryIds / SubcategoriesByCategory). Kept free of Angular/Firebase
 * so they can be unit tested directly.
 */

/**
 * The categories a program belongs to. Prefers the multi-category array and
 * falls back to the legacy singular CategoryId for un-migrated records.
 */
export function categoryIdsOf(program: Program): string[] {
  if (program.CategoryIds && program.CategoryIds.length) {
    return [...program.CategoryIds];
  }
  return program.CategoryId ? [program.CategoryId] : [];
}

/**
 * The subcategory ids selected under a single category, with legacy fallback.
 */
export function subcategoryIdsOf(program: Program, categoryId: string): string[] {
  if (
    program.SubcategoriesByCategory &&
    program.SubcategoriesByCategory[categoryId]
  ) {
    return [...program.SubcategoriesByCategory[categoryId]];
  }
  // Legacy single-category record: its subcategories belong to its one category.
  if (categoryId === program.CategoryId) {
    if (program.SubcategoryIds) {
      return [...program.SubcategoryIds];
    }
    return program.SubcategoryId ? [program.SubcategoryId] : [];
  }
  return [];
}

/**
 * The full category -> subcategory-ids map for a program, with legacy fallback.
 */
export function subcategoriesByCategoryOf(program: Program): {
  [categoryId: string]: string[];
} {
  const map: { [categoryId: string]: string[] } = {};
  if (program.SubcategoriesByCategory) {
    Object.keys(program.SubcategoriesByCategory).forEach((categoryId) => {
      map[categoryId] = [...program.SubcategoriesByCategory[categoryId]];
    });
    return map;
  }
  if (program.CategoryId) {
    map[program.CategoryId] = program.SubcategoryIds
      ? [...program.SubcategoryIds]
      : program.SubcategoryId
      ? [program.SubcategoryId]
      : [];
  }
  return map;
}

/**
 * The subcategory map limited to the currently-selected categories, so that
 * deselected categories drop out of the persisted record.
 */
export function pruneSubcategories(
  selectedCategoryIds: string[],
  subcategoriesByCategory: { [categoryId: string]: string[] }
): { [categoryId: string]: string[] } {
  const pruned: { [categoryId: string]: string[] } = {};
  selectedCategoryIds.forEach((categoryId) => {
    pruned[categoryId] = subcategoriesByCategory[categoryId] || [];
  });
  return pruned;
}

/**
 * The legacy singular fields mirrored off the primary (first) category, so
 * un-migrated clients keep working until they read the array fields.
 */
export function legacyMirror(
  selectedCategoryIds: string[],
  subcategoriesByCategory: { [categoryId: string]: string[] }
): { CategoryId: string; SubcategoryId: string | null; SubcategoryIds: string[] } {
  const primaryCategoryId = selectedCategoryIds[0] || '';
  const primarySubcategories = subcategoriesByCategory[primaryCategoryId] || [];
  return {
    CategoryId: primaryCategoryId,
    SubcategoryId: primarySubcategories[0] || null,
    SubcategoryIds: primarySubcategories,
  };
}

/**
 * Flatten a category -> subcategory-ids map into a single list, used to diff
 * selections for change tracking.
 */
export function flattenSubcategories(map: {
  [categoryId: string]: string[];
}): string[] {
  return Object.keys(map || {}).reduce(
    (acc, categoryId) => acc.concat(map[categoryId] || []),
    [] as string[]
  );
}
